import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdCheckCircle, MdClose, MdEventBusy, MdInventory2, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import { API_URL } from '../../Utils/url';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const daysAgo = (d) => {
  if (!d) return null;
  const n = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  return n <= 0 ? 'today' : n === 1 ? '1 day ago' : `${n} days ago`;
};
const img = (p) => (p ? (p.startsWith('http') ? p : `${API_URL.replace(/\/api\/?$/, '/')}${p.replace(/^\//, '')}`) : undefined);
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };

const Kpi = ({ title, value, hint, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 200, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
      {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85 }}>{hint}</Typography>}
    </Box>
  </Box>
);

const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.95, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
  </Box>
);

const StatusChip = ({ status }) => {
  const done = status === 'completed';
  return <Chip size="small" label={done ? 'Written off' : 'Pending'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: done ? '#dcfce7' : '#fef3c7', color: done ? '#166534' : '#92400e' }} />;
};

const EMPTY = { search: '', warehouseId: '', from: '', to: '' };

const ExpiredStock = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ pending: { records: 0, units: 0, value: 0 }, completed: { records: 0, units: 0, value: 0 } });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('pending');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, status: tab, search: debounced, warehouseId: filters.warehouseId, from: filters.from, to: filters.to };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('stock/expire/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.expired || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
      if (res?.warehouses) setWarehouses(res.warehouses);
    } catch {
      if (mine === seq.current) setError('Could not load expired stock.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, tab, debounced, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const dirty = !!search || Object.values(filters).some(Boolean);
  const valueOf = (r) => (Number(r.stockId?.purchasePrice) || 0) * (r.quantityExpired || 0);

  const s = stats;
  const sel = selected;
  const batch = sel?.stockId;
  const unitCost = Number(batch?.purchasePrice) || 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Expired stock</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Batches past their expiry date, waiting to be written off or already cleared.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Waiting to be written off" value={`${s.pending.units} units`} hint={`${s.pending.records} batch(es) · ${inr(s.pending.value)} at cost`} icon={MdEventBusy} from="#f59e0b" to="#b45309" active={tab === 'pending'} onClick={() => { setTab('pending'); setPage(1); }} />
        <Kpi title="Written off" value={`${s.completed.units} units`} hint={`${s.completed.records} batch(es) · ${inr(s.completed.value)} at cost`} icon={MdCheckCircle} from="#22c55e" to="#15803d" active={tab === 'completed'} onClick={() => { setTab('completed'); setPage(1); }} />
        <Kpi title="Total lost to expiry" value={inr(s.pending.value + s.completed.value)} hint={`${s.pending.units + s.completed.units} units overall`} icon={MdInventory2} from="#e11d48" to="#9f1239" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search product, UPC or batch number…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={tab} onChange={(e) => { setTab(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="pending">Waiting</MenuItem><MenuItem value="completed">Written off</MenuItem>
        </TextField>
        <TextField select size="small" label="Warehouse" value={filters.warehouseId} onChange={(e) => setF('warehouseId', e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All warehouses</MenuItem>
          {warehouses.map((w) => <MenuItem key={w._id} value={w._id}>{w.name}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="Expired from" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Expired to" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        {dirty && <Button onClick={() => { setSearch(''); setFilters(EMPTY); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Product</TableCell>
                <TableCell sx={head}>Batch</TableCell>
                <TableCell sx={head}>Warehouse</TableCell>
                <TableCell sx={head}>Expired on</TableCell>
                <TableCell sx={head} align="right">Quantity</TableCell>
                <TableCell sx={head} align="right">Value at cost</TableCell>
                <TableCell sx={head}>{tab === 'completed' ? 'Written off' : 'Status'}</TableCell>
                <TableCell sx={head} align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(8)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No records match these filters.' : tab === 'pending' ? 'Nothing is waiting to be written off.' : 'Nothing has been written off yet.'}</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r._id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                  <TableCell sx={{ ...cell, maxWidth: 280 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar variant="rounded" src={img(r.productId?.product_images?.[0])} sx={{ width: 38, height: 38, bgcolor: '#fee2e2', color: '#991b1b' }}><MdInventory2 size={18} /></Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }} title={r.productId?.title}>{r.productId?.title || 'Product removed'}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{r.productId?.upc || r.expId || ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>{r.stockId?.batch_no || '—'}</TableCell>
                  <TableCell sx={cell}>{r.stockId?.warehouseId?.name || '—'}</TableCell>
                  <TableCell sx={cell}>
                    <b style={{ color: '#b91c1c' }}>{fmtDate(r.expiryDate)}</b>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>{daysAgo(r.expiryDate)}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cell, fontWeight: 800, color: '#b91c1c' }} align="right">{r.quantityExpired || 0}</TableCell>
                  <TableCell sx={cell} align="right">{valueOf(r) ? inr(valueOf(r)) : '—'}</TableCell>
                  <TableCell sx={cell}>{tab === 'completed' ? fmtDate(r.completedAt) : <StatusChip status={r.status} />}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View details"><IconButton size="small" onClick={() => setSelected(r)} sx={{ color: '#4f46e5', background: '#4f46e514', borderRadius: '8px' }}><MdVisibility size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} record{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* details */}
      <Dialog open={!!sel} onClose={() => setSelected(null)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {sel && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#e11d48,#9f1239)', color: '#fff' }}>
              <Avatar variant="rounded" src={img(sel.productId?.product_images?.[0])} sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,.2)' }}><MdInventory2 /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{sel.productId?.title || 'Product removed'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>Batch {batch?.batch_no || '—'} · expired {fmtDate(sel.expiryDate)} ({daysAgo(sel.expiryDate)})</Typography>
              </Box>
              <StatusChip status={sel.status} />
              <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
              <Card title="Loss" accent="#e11d48">
                <Box sx={{ p: 2, borderRadius: '12px', background: '#fff1f2', border: '1px solid #fecdd3', mb: 1 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#9f1239' }}>VALUE AT COST</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, color: '#881337' }}>{unitCost ? inr(unitCost * (sel.quantityExpired || 0)) : '—'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#be123c' }}>{sel.quantityExpired || 0} unit(s){unitCost ? ` × ${inr(unitCost)}` : ''}</Typography>
                </Box>
                <Row label="Selling value (MRP)" value={batch?.maximumRetailPrice ? inr(batch.maximumRetailPrice * (sel.quantityExpired || 0)) : null} />
                <Row label="Reason" value={sel.reason} />
              </Card>
              <Card title="Batch" accent="#f59e0b">
                <Row label="Batch number" value={batch?.batch_no} />
                <Row label="Received quantity" value={batch?.quantity} />
                <Row label="Sold before expiry" value={batch?.soldQuantity ?? 0} />
                <Row label="Left to expire" value={sel.quantityExpired} />
                <Row label="Manufactured" value={fmtDate(batch?.manufactureDate)} />
                <Row label="Expired" value={fmtDate(sel.expiryDate)} />
                <Row label="Received from" value={batch?.receivedFrom} />
              </Card>
              <Card title="Product" accent="#6366f1">
                <Row label="UPC" value={sel.productId?.upc} />
                <Row label="Brand" value={typeof sel.productId?.brand === 'string' ? sel.productId.brand : sel.productId?.brand?.name} />
                <Row label="Pack" value={sel.productId?.unitValue ? `${sel.productId.unitValue} ${sel.productId.unitName || ''}`.trim() : null} />
                <Row label="Ownership" value={sel.productId?.ownership} />
                <Row label="Stock still on sale" value={sel.productId?.availableQuantity} />
              </Card>
              <Card title="Warehouse" accent="#0ea5e9">
                <Row label="Name" value={batch?.warehouseId?.name} />
                <Row label="Code" value={batch?.warehouseId?.code} />
                <Row label="Contact" value={batch?.warehouseId?.contactPerson} />
                <Row label="Phone" value={batch?.warehouseId?.phoneNumber} />
              </Card>
              <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                <Card title={`Where it was stored (${(sel.expLocations || []).length})`} accent="#22c55e">
                  {(sel.expLocations || []).length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No rack locations were recorded for this batch.</Typography>}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(sel.expLocations || []).map((l, i) => (
                      <Chip key={i} label={`Rack ${l.rackId?.id || '—'} · section ${l.section ?? '—'} · shelf ${l.shelf ?? '—'} · ${l.aloquantity ?? 0} unit(s)`} sx={{ fontFamily: FONT, fontWeight: 700, background: '#e0e7ff', color: '#3730a3' }} />
                    ))}
                  </Box>
                </Card>
              </Box>
              <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                <Card title="History" accent="#94a3b8">
                  <Row label="Record" value={sel.expId} />
                  <Row label="Flagged expired" value={dt(sel.createdAt)} />
                  <Row label="Marked by" value={sel.markedBy} />
                  <Row label="Written off" value={sel.status === 'completed' ? dt(sel.completedAt) : 'Not yet'} />
                </Card>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setSelected(null)} variant="contained" sx={yellowBtn}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ExpiredStock;
